import React, { useEffect, useRef, useState } from 'react';
import { Pressable, PressableProps } from 'react-native';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { Maybe } from 'shared/utils/types';

interface PressableWithDelayProps extends PressableProps {
  onPress: () => void;
  analyticsId?: string;
  customAttributes?: Maybe<Record<string, unknown> | null>;
  delay?: number;
}

/**
 * PressableWithDelay component adds a delay before handling the press action.
 * Use this instead of the regular Pressable component.
 */

const PressableWithDelay = ({
  onPress,
  analyticsId,
  customAttributes,
  delay = 500,
  role = 'button',
  ...rest
}: PressableWithDelayProps) => {
  const [isPressed, setIsPressed] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buttonAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  const handleOnPress = () => {
    setIsPressed(true);
    buttonAnalytics();

    onPress();

    timeoutRef.current = setTimeout(() => {
      setIsPressed(false);
      timeoutRef.current = null;
    }, delay);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Pressable
      {...rest}
      onPress={handleOnPress}
      disabled={isPressed || rest.disabled}
      role={role}
    />
  );
};

export default PressableWithDelay;
